import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

/**
 * AuthCallback handles the Emergent Auth (Google OAuth) redirect.
 *
 * Flow:
 *   1. Emergent Auth redirects user to https://<origin>/dashboard#session_id=<id>
 *   2. App.js detects `session_id=` in the URL hash and renders this component.
 *   3. We extract the session_id, immediately clear the hash so a refresh /
 *      back-button cannot replay an already-consumed token, then exchange the
 *      session_id at POST /api/auth/session.
 *
 * Common failure modes we surface clearly:
 *   - Network failure ("Failed to fetch") → user sees a Retry button.
 *   - Emergent Auth returns user_data_not_found (token expired or already used)
 *     → user sees a "Sign in again" CTA instead of a useless toast.
 *   - Non-JSON error body (e.g. CF/ingress 5xx HTML) → graceful text read.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null); // { code, message }

  useEffect(() => {
    // Guard against double-execution within the same mount (e.g. StrictMode).
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuthCallback = async () => {
      // 1) Read the session_id, then immediately clear the hash so a refresh
      //    or back-navigation can't replay the now-consumed token.
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
      const sessionId = params.get('session_id');
      // Clear the hash NOW (before any await). Use replaceState so we don't
      // create a history entry the user can step back into.
      try {
        window.history.replaceState(null, '', window.location.pathname);
      } catch (_) { /* non-fatal */ }

      if (!sessionId) {
        setError({
          code: 'no_session_id',
          message: 'No session ID was returned by Google. Please sign in again.',
        });
        return;
      }

      console.log('🔑 Processing OAuth callback');

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

      let response;
      try {
        response = await fetch(`${backendUrl}/api/auth/session`, {
          method: 'POST',
          headers: {
            'X-Session-ID': sessionId,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
      } catch (networkErr) {
        // Pure network failure (CORS, offline, DNS, blocked by extension, etc.)
        console.error('❌ OAuth network error:', networkErr);
        setError({
          code: 'network',
          message:
            'We could not reach the server to complete sign-in. Check your internet connection and try again.',
        });
        return;
      }

      if (!response.ok) {
        // Best-effort parse: response may be JSON, plain text, or HTML.
        let detail = '';
        let parsedJson = null;
        try {
          const txt = await response.text();
          try {
            parsedJson = JSON.parse(txt);
            detail = typeof parsedJson?.detail === 'string'
              ? parsedJson.detail
              : JSON.stringify(parsedJson?.detail || parsedJson);
          } catch (_) {
            detail = txt.slice(0, 200);
          }
        } catch (_) { /* response body unreadable */ }

        console.warn(`OAuth exchange failed: HTTP ${response.status} — ${detail}`);

        // Emergent Auth tells us when the session_id was already consumed or
        // expired. Surface a clear, actionable message in that case.
        const lower = (detail || '').toLowerCase();
        if (
          response.status === 404 ||
          lower.includes('user_data_not_found') ||
          lower.includes('expired')
        ) {
          setError({
            code: 'expired',
            message:
              'This sign-in link has already been used or expired. Please sign in again to start a fresh session.',
          });
          return;
        }
        if (response.status >= 500) {
          setError({
            code: 'server',
            message: 'The sign-in service is having trouble right now. Please try again in a moment.',
          });
          return;
        }
        setError({
          code: 'unknown',
          message: detail || `Sign-in failed (HTTP ${response.status}).`,
        });
        return;
      }

      // Success path
      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        console.error('OAuth success but body unparseable:', parseErr);
        setError({
          code: 'parse',
          message: 'Sign-in completed but the server response was malformed. Please try again.',
        });
        return;
      }

      console.log('✅ OAuth authentication successful:', data?.user?.email);
      login(data.user);
      toast.success(`Welcome back, ${data.user.name || data.user.email}!`);
      navigate('/app', { replace: true });
    };

    processOAuthCallback();
  }, [navigate, login]);

  // Error fallback UI — keeps the user on a recognisable VehicleBuddy page with
  // a clear next step instead of bouncing silently to /login.
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <div className="max-w-md w-full bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center text-white border border-white/10 shadow-xl">
          <div className="mx-auto w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
            <AlertCircle className="w-7 h-7 text-red-300" />
          </div>
          <h1 className="text-2xl font-semibold mb-2" data-testid="auth-callback-error-title">
            Sign-in didn&apos;t complete
          </h1>
          <p className="text-sm text-white/80 mb-6" data-testid="auth-callback-error-message">
            {error.message}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/login', { replace: true })}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="auth-callback-signin-again"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Sign in again
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/', { replace: true })}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="auth-callback-home"
            >
              Back to home
            </Button>
          </div>
          <p className="text-xs text-white/40 mt-6">Error code: {error.code}</p>
        </div>
      </div>
    );
  }

  // Loading state
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900"
      data-testid="auth-callback-loading"
    >
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400 mb-4"></div>
        <p className="text-white text-lg">Completing sign in…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
